import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { SURVEILLANCE_ALERT_LISTS_QUERY_KEY, SURVEILLANCE_ALERT_QUERY_KEY } from '@/hooks/queries/market-surveillance/use-surveillance-alerts';
import {
  addAlertNote,
  type ChangeAlertStatusRequest,
  changeAlertStatus,
  getAlertAudit,
  getAlertRelated,
  getSurveillanceAlert,
  mapAlertDetail,
  mapAlertRelated,
  mapAuditEntries,
  patchRawDetailAfterStatusChange,
  patchRawListRowStatus,
  prependRawAuditEntry,
  type RawSurveillanceAlertDetail,
  type RawSurveillanceAlertsResponse,
  type RawSurveillanceAuditResponse,
  syntheticNoteAuditEntry,
  syntheticStatusAuditEntry,
} from '@/services/api/market-surveillance';

export const surveillanceAlertQueryOptions = (alertId: string) =>
  queryOptions({
    queryKey: [...SURVEILLANCE_ALERT_QUERY_KEY, alertId, 'detail'],
    queryFn: () => getSurveillanceAlert(alertId),
    select: mapAlertDetail,
  });

export const useSurveillanceAlert = (alertId: string) => useQuery(surveillanceAlertQueryOptions(alertId));

export const alertAuditQueryOptions = (alertId: string) =>
  queryOptions({
    queryKey: [...SURVEILLANCE_ALERT_QUERY_KEY, alertId, 'audit'],
    queryFn: () => getAlertAudit(alertId),
    select: mapAuditEntries,
  });

export const useAlertAudit = (alertId: string) => useQuery(alertAuditQueryOptions(alertId));

export const alertRelatedQueryOptions = (alertId: string) =>
  queryOptions({
    queryKey: [...SURVEILLANCE_ALERT_QUERY_KEY, alertId, 'related'],
    queryFn: () => getAlertRelated(alertId),
    select: mapAlertRelated,
  });

export const useAlertRelated = (alertId: string) => useQuery(alertRelatedQueryOptions(alertId));

// Writes patch caches from the response, then mark queries stale WITHOUT refetching
// (refetchType 'none'): bop-api writes via the primary but reads via the replica, so an
// immediate refetch races replication and pins the OLD state for the whole staleTime.
// The next natural refetch (tab focus, remount) lands after replication has caught up.
const markStaleAfterWrite = (queryClient: ReturnType<typeof useQueryClient>, alertId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: [...SURVEILLANCE_ALERT_QUERY_KEY, alertId], refetchType: 'none' }),
    queryClient.invalidateQueries({ queryKey: SURVEILLANCE_ALERT_LISTS_QUERY_KEY, refetchType: 'none' }),
  ]);

export const useChangeAlertStatus = (alertId: string) => {
  const queryClient = useQueryClient();
  return async (body: ChangeAlertStatusRequest): Promise<void> => {
    const response = await changeAlertStatus(alertId, body);

    queryClient.setQueryData<RawSurveillanceAlertDetail>([...SURVEILLANCE_ALERT_QUERY_KEY, alertId, 'detail'], (raw) => patchRawDetailAfterStatusChange(raw, response));
    queryClient.setQueryData<RawSurveillanceAuditResponse>([...SURVEILLANCE_ALERT_QUERY_KEY, alertId, 'audit'], (raw) =>
      prependRawAuditEntry(raw, syntheticStatusAuditEntry(response, body.assessment)),
    );
    queryClient.setQueriesData<RawSurveillanceAlertsResponse>({ queryKey: SURVEILLANCE_ALERT_LISTS_QUERY_KEY }, (raw) => patchRawListRowStatus(raw, alertId, response.new_status));

    await markStaleAfterWrite(queryClient, alertId);
  };
};

export const useAddAlertNote = (alertId: string) => {
  const queryClient = useQueryClient();
  return async (body: { note: string }): Promise<void> => {
    const response = await addAlertNote(alertId, body);

    queryClient.setQueryData<RawSurveillanceAuditResponse>([...SURVEILLANCE_ALERT_QUERY_KEY, alertId, 'audit'], (raw) =>
      prependRawAuditEntry(raw, syntheticNoteAuditEntry(response, body.note)),
    );

    await markStaleAfterWrite(queryClient, alertId);
  };
};
